'use client';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { loadCommunityVotes, setCommunityVote } from '@wt/app/community/utils';
import { PuzzleResultsState } from '@wt/app/game/_components/types';
import { usePuzzleTodays } from '@wt/app/game/_hooks/usePuzzleTodays';
import { getGameScore, getMaxScore } from '@wt/app/game/_utils/getGameScore';
import { getHintType } from '@wt/game/providers/hints/HintsProvider';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { getDayString } from '@wt/game/utils/getDayString';
import { getNumberEmoji } from '@wt/game/utils/getNumberEmoji';
import getPuzzleNumber from '@wt/game/utils/getPuzzleNumber';
import { useCopyToClipboard } from '@wt/shared/hooks/useCopyClipboard';
import useReactive from '@wt/shared/hooks/useReactive';
import { getSiteMode, getSiteName, getSiteURL } from '@wt/utilities/siteMode';
import dayjs from 'dayjs';
import Link from 'next/link';
import { redirect } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { browserName, isDesktop, osName } from 'react-device-detect';
import { toast } from 'react-toastify';
import useWebShare from 'react-use-web-share';

import { GameModeTag } from './GameModeTag';
import useSettingsContext from '@wt/game/providers/settings/types';

export const getScoreIcon = (score: number) => {
  if (score >= 175) {
    return '🥇';
  }
  if (score >= 125) {
    return '🥈';
  }
  if (score >= 75) {
    return '🥉';
  }
  if (getSiteMode() == 'whentaken-movies') {
    return '🙈';
  }
  return '🎗️';
};

export const RoundSummary = () => {
  const { gameMode, puzzleId, puzzle } = useRoundProgress();
  const [settings] = useSettingsContext();

  const state: PuzzleResultsState & {
    locationScore: number;
    yearScore: number;
  } = useReactive({
    date: puzzleId ?? getDayString(0),
    guesses: undefined,
    device: '',
    score: 0,
    round: -1,
    puzzle,
    locationScore: 0,
    yearScore: 0,
  });
  const [loaded, setLoaded] = useState<boolean>(false);
  const { isSupported, share } = useWebShare();

  const [voteDelta, setVoteDelta] = useState(0);

  const roundProgress = useRoundProgress();
  const [todays] = usePuzzleTodays(
    puzzleId ?? state.date,
    roundProgress.puzzle.length,
    gameMode
  );

  const isDaily = gameMode == 'daily';
  const suffix =
    !isDaily && puzzleId ? `/${gameMode}/game/${puzzleId}` : '/game';
  useEffect(() => {
    if (todays && todays.guesses) {
      if (todays.guesses && todays.guesses.length === 0) {
        return redirect(suffix);
      }
      state.guesses = todays.guesses;
      state.score =
        todays.guesses &&
        todays.guesses.reduce((c, pg) => {
          const ps = getGameScore(pg);
          return c + ps.score.total;
        }, 0);
      state.locationScore =
        todays.guesses &&
        todays.guesses.reduce((c, pg) => {
          const ps = getGameScore(pg);
          return c + ps.score.distance;
        }, 0);
      state.yearScore =
        todays.guesses &&
        todays.guesses.reduce((c, pg) => {
          const ps = getGameScore(pg);
          return c + ps.score.year;
        }, 0);
      state.round = todays.guesses.length;
      setLoaded(true);
      const allVotes = loadCommunityVotes();
      if (gameMode == 'community' && allVotes[puzzleId ?? '']) {
        setVoteDelta(allVotes[puzzleId ?? '']);
      }
    }
  }, [todays]);

  const isLastRound =
    state.guesses && state.guesses.length === state.puzzle.length;

  const shareText = useMemo(() => {
    let gameInfo = '';
    if (gameMode !== 'community') {
      const puzzleNumber = getPuzzleNumber(state.date);
      const date = dayjs(state.date).format('DD.MM.YYYY');
      gameInfo = `#${puzzleNumber} (${date})`;
    } else {
      gameInfo = `#Community\n${getSiteURL()}/community/game/${puzzleId}`;
    }

    let hintsUsed = 0;
    let hintsUsedEmoji = '';
    if (todays.guesses) {
      for (const g of todays.guesses) {
        if (g.hintsUsed && g.hintsUsed.length > 0) {
          hintsUsed += g.hintsUsed.length;
        }
      }
    }
    if (hintsUsed > 0) {
      hintsUsedEmoji = ` (💡x${hintsUsed}) `;
    }

    let scoreEmoji = '🎉';
    let siteEmoji = '';
    const scoreForEmoji = state.score;
    if (getSiteMode() == 'whentaken-movies') {
      siteEmoji = ' 🎬';
      if (scoreForEmoji >= 800) {
        scoreEmoji = '🎟️🍿🥤🍫';
      } else if (scoreForEmoji >= 650) {
        scoreEmoji = '🎟️🍿🥤';
      } else if (scoreForEmoji >= 550) {
        scoreEmoji = '🎟️🍿';
      } else if (scoreForEmoji >= 375) {
        scoreEmoji = '🎟️';
      } else {
        scoreEmoji = '👎👎';
      }
    } else {
      if (scoreForEmoji >= 900) {
        scoreEmoji = '👑';
      } else if (scoreForEmoji >= 850) {
        scoreEmoji = '🏆';
      } else if (scoreForEmoji >= 750) {
        scoreEmoji = '🏅';
      } else if (scoreForEmoji >= 100) {
        scoreEmoji = '🎗️';
      } else {
        scoreEmoji = '💩';
      }
    }

    const title = `#${getSiteName()}${siteEmoji} ${gameInfo}\n\nI scored ${state.score}/${
      getMaxScore() * puzzle.length
    }${hintsUsedEmoji}${scoreEmoji}\n`;

    const description =
      todays.guesses &&
      todays.guesses.reduce((s, g, i) => {
        const ps = getGameScore(g, settings?.distanceUnit);
        const pin = `📍`;
        const sicon = getScoreIcon(ps.score.total);
        const calendar = `🗓️`;
        let usedLocationHint = false;
        let usedYearHint = false;
        let line = '';
        if (g.hintsUsed && g.hintsUsed.length > 0) {
          for (let i = 0; i < g.hintsUsed.length; i++) {
            const hintType = getHintType(g.hintsUsed[i]);
            if (hintType == 'location') {
              usedLocationHint = true;
            } else if (hintType == 'year') {
              usedYearHint = true;
            }
          }
        }
        if (getSiteMode() != 'wheretaken') {
          line =
            getNumberEmoji(i + 1) +
            `${pin}${usedLocationHint ? '💡' : ''}${ps.distanceTextAbbr} - ${calendar}${usedYearHint ? '💡' : ''}${ps.yearsOff} yrs - ${sicon}${ps.score.total}/${getMaxScore()}\n`;
        } else {
          line =
            getNumberEmoji(i + 1) +
            `${pin}${usedLocationHint ? '💡' : ''}${ps.distanceTextAbbr} - ${sicon}${ps.score.total}/${getMaxScore()}\n`;
        }
        return s + line;
      }, '');
    return `${title}\n${description}`;
  }, [state.score, todays.guesses, settings?.distanceUnit]);

  const [, copyShareText] = useCopyToClipboard();
  const sharePuzzle = useCallback(() => {
    const newShare = shareText;

    // Firefox Android does not support navigator.share with text
    const isFirefoxAndroid = browserName === 'Firefox' && osName === 'Android';

    if (isSupported && !isFirefoxAndroid && !isDesktop) {
      share({
        title: getSiteName(),
        text: newShare,
        url: getSiteURL(),
      });
    } else {
      copyShareText(`${newShare}\n${getSiteURL()}`).then((c) => {
        toast(c ? 'Copied to clipboard' : 'Failed to copy to clipboard', {
          type: c ? 'success' : 'error',
        });
      });
    }
  }, [loaded, shareText, settings?.distanceUnit]);

  useEffect(() => {
    if (!isLastRound && loaded) {
      return redirect(suffix + '/results');
    }
  }, [isLastRound, loaded]);

  if (!todays || !loaded) {
    return <></>;
  }

  let voteButtonClass =
    'btn mr-2 inline-flex items-center align-center border border-gray-400 text-lg text-base-100 md:h-[61px] md:text-xl bg-base-300 hover:bg-base-300/80';
  if (voteDelta != 0) {
    voteButtonClass += ' pointer-events-none';
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-6 p-4 md:gap-9 md:px-12  lg:gap-12 desktopShort:gap-6">
      <GameModeTag />
      {gameMode == 'community' && roundProgress.communityGame && (
        <>
          <div className="text-center">
            <div className="text-xl">
              {voteDelta != 0
                ? 'Thank you for rating this game!'
                : 'Rate this game'}
            </div>
            <div className="my-3">
              <div
                className={
                  voteButtonClass + (voteDelta == -1 ? ' bg-lime-600' : '')
                }
                onClick={() => {
                  if (voteDelta == 0) {
                    setCommunityVote(puzzleId ?? '', -1);
                    setVoteDelta(-1);
                  }
                }}
              >
                <span className="pointer-events-none whitespace-nowrap">
                  💩
                </span>
              </div>
              <div
                className={
                  voteButtonClass + (voteDelta == 1 ? ' bg-green-400' : '')
                }
                onClick={() => {
                  if (voteDelta == 0) {
                    setCommunityVote(puzzleId ?? '', 1);
                    setVoteDelta(1);
                  }
                }}
              >
                <span className="pointer-events-none whitespace-nowrap">
                  🔥
                </span>
              </div>
            </div>
            <div>
              <Link
                href={`/community?username=${roundProgress.communityGame?.username}`}
                className="italic text-highlight dark:text-highlight-dark"
              >
                More public games by @{roundProgress.communityGame?.username}
              </Link>
            </div>
          </div>
          <div className="text-xl">Your score</div>
        </>
      )}
      <div className="hidden w-full max-w-lg flex-row items-center justify-center gap-6 md:flex">
        {getSiteMode() != 'wheretaken' && (
          <>
            <ScoreContainer
              emoji={'📍'}
              score={state.locationScore}
              label={'Location'}
            />

            <ScoreContainer
              emoji={'🗓️'}
              score={state.yearScore}
              label={'Year'}
            />
          </>
        )}
        <ScoreContainer
          emoji={'⚡️'}
          score={state.score}
          label={getSiteMode() != 'wheretaken' ? 'Total' : 'Score'}
        />
      </div>
      <div className="flex w-4/5 flex-row items-center justify-center gap-2 md:hidden">
        <ScoreContainer
          emoji={'⚡️'}
          score={state.score}
          label={getSiteMode() != 'wheretaken' ? 'Total' : 'Score'}
          size="large"
        />
        {getSiteMode() != 'wheretaken' && (
          <div className="flex w-1/2 flex-col gap-2">
            <ScoreContainer
              emoji={'📍'}
              score={state.locationScore}
              label={'Location'}
              size="small"
            />
            <ScoreContainer
              emoji={'🗓️'}
              score={state.yearScore}
              label={'Year'}
              size="small"
            />
          </div>
        )}
      </div>
      <div className="flex w-full max-w-[360px] flex-wrap items-center justify-center md:max-w-[540px]">
        {puzzle &&
          puzzle.map((p, i) => {
            return (
              <div className="mb-2 w-1/3 px-1 md:px-2" key={i}>
                <Link
                  key={'round-summary-' + i}
                  href={suffix + '/results/' + (i + 1)}
                  className="flex items-center justify-center gap-3 rounded-[48px] border border-accent bg-base-200 p-3 shadow hover:text-accent"
                >
                  <div className="text-center text-sm md:text-lg">
                    Round {i + 1}
                  </div>
                </Link>
              </div>
            );
          })}
      </div>
      {getSiteMode() == 'whentaken' && state.date === '2026-06-16' && (
        <div className="flex w-full max-w-[360px] flex-col items-center rounded-2xl bg-[#E8F5E9] md:max-w-[540px]">
          <div className="w-full rounded-t-2xl bg-[#4CAF50] px-3 py-2 text-center text-sm font-semibold text-white md:text-base">
            ⚽️ Did you enjoy today&apos;s game?
          </div>
          <div className="px-3 py-2 text-center text-sm text-[#4B4554] md:text-base">
            Why don&apos;t you try{' '}
            <a
              href="https://worldcuple.com?utm_source=whentaken&utm_medium=cross_promotion&utm_campaign=worldcuple_2026"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline"
            >
              Worldcuple
            </a>{' '}
            for more World Cup fun!
          </div>
        </div>
      )}
      <div className="hidden flex-row gap-10 md:flex">
        <PlayArchiveButton />
        <ShareResultsButton sharePuzzle={sharePuzzle} />
      </div>
      <div className="flex flex-col gap-5 md:hidden">
        <ShareResultsButton sharePuzzle={sharePuzzle} />
        <PlayArchiveButton />
      </div>
    </div>
  );
};

const ShareResultsButton = ({ sharePuzzle }: { sharePuzzle: () => void }) => {
  return (
    <button
      onClick={sharePuzzle}
      className="btn btn-primary w-full shrink whitespace-nowrap text-lg font-bold uppercase text-white movies:text-[#2E2C26] md:h-[61px] md:text-xl"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <ShareOutlinedIcon className="pr-1" /> share results
      </span>
    </button>
  );
};

const PlayArchiveButton = () => {
  return (
    <Link
      href={'/archive'}
      className="btn w-full shrink border border-gray-400 bg-base-300 text-lg font-bold uppercase text-base-100 hover:bg-base-300/80 md:h-[61px] md:text-xl"
      onClick={() => {
        window.gtag &&
          window.gtag('event', 'archive', {
            message: 'archive from summary screen',
          });
      }}
    >
      <span className="pointer-events-none whitespace-nowrap">
        <ArchiveOutlinedIcon className="pr-1" /> play archive
      </span>
    </Link>
  );
};

const ScoreContainer = ({
  emoji,
  score,
  label,
  size,
}: {
  emoji: string;
  score: number;
  label: string;
  size?: 'small' | 'large' | undefined;
}) => {
  let containerClasses =
    'flex w-full flex-col items-center justify-center gap-2 rounded-2xl bg-base-200 px-3 py-4 text-center md:p-4';
  let scoreClasses = 'text-xl font-bold md:text-3xl';
  let labelClasses = 'text-sm md:text-base';

  if (size == 'large') {
    containerClasses += ' h-full';
    scoreClasses = 'text-4xl font-bold';
  } else if (size == 'small') {
    containerClasses =
      'flex w-full flex-col items-center justify-center gap-1 rounded-2xl bg-base-200 px-3 py-1 text-center';
    scoreClasses = 'text-sm font-bold';
    labelClasses = 'text-xs';
  }

  return (
    <div className={containerClasses}>
      <span className={scoreClasses}>{`${emoji} ${score}`}</span>
      <span className={labelClasses}>{label}</span>
    </div>
  );
};
